import type { JsonObject, JsonValue, WorldDocumentKind } from "./types";

export const WORLD_CARD_SUGGESTION_KINDS = ["character", "item", "skill", "location"] as const;
export type WorldCardSuggestionKind = (typeof WORLD_CARD_SUGGESTION_KINDS)[number];
export type WorldCardSuggestionStatus = "pending" | "accepted" | "dismissed";

export interface WorldCardSuggestion {
  id: string;
  storyId: string;
  kind: WorldCardSuggestionKind;
  title: string;
  summary: string;
  fields: JsonObject;
  sourceMessageIds: string[];
  status: WorldCardSuggestionStatus;
  createdAt: string;
  acceptedDocumentId?: string;
}

const isObject = (value: unknown): value is Record<string, unknown> => Boolean(value && typeof value === "object" && !Array.isArray(value));
const toJson = (value: unknown): JsonValue => {
  if (value == null || typeof value === "string" || typeof value === "number" || typeof value === "boolean") return value as JsonValue;
  if (Array.isArray(value)) return value.slice(0, 64).map(toJson);
  if (isObject(value)) return Object.fromEntries(Object.entries(value).slice(0, 64).filter(([, item]) => item !== undefined).map(([key, item]) => [key, toJson(item)]));
  return String(value);
};

function jsonArray(raw: string): unknown[] {
  const cleaned = raw.trim().replace(/^```(?:json)?\s*/i, "").replace(/\s*```$/i, "");
  const start = cleaned.indexOf("[");
  const end = cleaned.lastIndexOf("]");
  if (start < 0 || end < start) return [];
  try { const value = JSON.parse(cleaned.slice(start, end + 1)); return Array.isArray(value) ? value : []; } catch { return []; }
}

/** Parses a bounded assistant proposal. Invalid or unsupported entities disappear instead of becoming canon. */
export function parseWorldCardSuggestions(
  raw: string,
  storyId: string,
  sourceMessageIds: readonly string[],
  createdAt: string,
  createId: () => string = () => crypto.randomUUID(),
): WorldCardSuggestion[] {
  const seen = new Set<string>();
  const output: WorldCardSuggestion[] = [];
  for (const item of jsonArray(raw).slice(0, 4)) {
    if (!isObject(item) || !WORLD_CARD_SUGGESTION_KINDS.includes(item.kind as WorldCardSuggestionKind)) continue;
    const title = typeof item.title === "string" ? item.title.trim().slice(0, 240) : "";
    if (!title) continue;
    const identity = `${String(item.kind)}:${title.toLocaleLowerCase()}`;
    if (seen.has(identity)) continue;
    seen.add(identity);
    output.push({
      id: createId(), storyId, kind: item.kind as WorldCardSuggestionKind, title,
      summary: typeof item.summary === "string" ? item.summary.trim().slice(0, 2_000) : "",
      fields: isObject(item.fields) ? toJson(item.fields) as JsonObject : {},
      sourceMessageIds: sourceMessageIds.filter(Boolean).slice(0, 16), status: "pending", createdAt,
    });
  }
  return output;
}

/** Restores durable proposals without trusting arbitrary account JSON. */
export function normalizeWorldCardSuggestions(value: unknown, storyId = ""): WorldCardSuggestion[] {
  if (!Array.isArray(value)) return [];
  const seen = new Set<string>();
  const output: WorldCardSuggestion[] = [];
  for (const item of value.slice(-100)) {
    if (!isObject(item) || !WORLD_CARD_SUGGESTION_KINDS.includes(item.kind as WorldCardSuggestionKind)) continue;
    const id = typeof item.id === "string" ? item.id.trim() : "";
    const title = typeof item.title === "string" ? item.title.trim().slice(0, 240) : "";
    const itemStoryId = typeof item.storyId === "string" && item.storyId.trim() ? item.storyId.trim() : storyId;
    const status = item.status === "accepted" || item.status === "dismissed" ? item.status : "pending";
    if (!id || !title || !itemStoryId || seen.has(id)) continue;
    seen.add(id);
    output.push({
      id, storyId: itemStoryId, kind: item.kind as WorldCardSuggestionKind, title,
      summary: typeof item.summary === "string" ? item.summary.trim().slice(0, 2_000) : "",
      fields: isObject(item.fields) ? toJson(item.fields) as JsonObject : {},
      sourceMessageIds: Array.isArray(item.sourceMessageIds) ? item.sourceMessageIds.filter((sourceId): sourceId is string => typeof sourceId === "string" && Boolean(sourceId)).slice(0, 16) : [],
      status, createdAt: typeof item.createdAt === "string" ? item.createdAt : new Date(0).toISOString(),
      ...(typeof item.acceptedDocumentId === "string" && item.acceptedDocumentId.trim() ? { acceptedDocumentId: item.acceptedDocumentId.trim() } : {}),
    });
  }
  return output;
}

export function suggestionKindLabel(kind: WorldDocumentKind): string {
  return kind === "character" ? "Character" : kind === "item" ? "Item" : kind === "skill" ? "Skill" : kind === "location" ? "Location" : kind;
}
